import { useCallback, useEffect, useMemo } from 'react'
import { useVariant } from '@unleash/proxy-client-react'
import bugsnagClient from 'config/bugsnag'
import { logExperimentDebug } from 'utils/experiments'
import { pinExperimentVariant } from 'utils/experimentStorage'
import { Feature } from 'utils/feature'
import { isMaestroTest } from 'utils/maestro'
import { ExperimentEvent } from './events'
import { useEventTracking } from './useEventTracking'

export const EXPOSURE_KEY_PREFIX = 'nutrisense:experiment_viewed:'

const reportExperimentIssue = (message: string) => {
  bugsnagClient.notify(new Error(message))
}

export const hasTrackedExposure = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) !== null
  } catch {
    return false
  }
}

export const markExposureTracked = (key: string): void => {
  try {
    sessionStorage.setItem(key, 'true')
  } catch {
    // Storage unavailable; proceed without dedupe.
  }
}

const isOriginalVariant = (variantName: string | undefined) => variantName === 'original'

const parsePayload = <TPayload>(
  payloadType: string | undefined,
  payloadValue: string | undefined
): { payload: TPayload | undefined; hasInvalidJsonPayload: boolean } => {
  if (!payloadValue) {
    return { payload: undefined, hasInvalidJsonPayload: false }
  }

  if (payloadType !== 'json') {
    return {
      payload: payloadValue as TPayload,
      hasInvalidJsonPayload: false
    }
  }

  try {
    return {
      payload: JSON.parse(payloadValue) as TPayload,
      hasInvalidJsonPayload: false
    }
  } catch {
    return { payload: undefined, hasInvalidJsonPayload: true }
  }
}

export const useExperiment = <TPayload = unknown>(
  feature: Feature,
  options?: { enabled?: boolean; variantOverride?: string }
) => {
  const variant = useVariant(feature)
  const track = useEventTracking()

  const variantName = options?.variantOverride ?? variant.name
  const payloadValue = variant.payload?.value
  const payloadType = variant.payload?.type
  const isActive = variant.enabled && !isMaestroTest() && (options?.enabled ?? true)
  const shouldReportIssues = isActive && !isOriginalVariant(variantName)

  useEffect(() => {
    if (isMaestroTest()) {
      return
    }

    logExperimentDebug(`resolved "${feature}"`, {
      enabled: isActive,
      variant: variantName,
      payload: payloadValue
    })
  }, [feature, isActive, variantName, payloadValue])

  useEffect(() => {
    if (!isActive) {
      return
    }
    if (!variantName) {
      return
    }

    pinExperimentVariant(feature, variantName)

    const exposureKey = `${EXPOSURE_KEY_PREFIX}${feature}:${variantName}`

    if (hasTrackedExposure(exposureKey)) {
      logExperimentDebug(`exposure already tracked this session for "${feature}"`)
      return
    }

    markExposureTracked(exposureKey)

    logExperimentDebug(`tracking "${ExperimentEvent.Viewed}"`, {
      experiment_name: feature,
      variation_name: variantName
    })

    track(ExperimentEvent.Viewed, {
      experiment_name: feature,
      variation_name: variantName
    })
  }, [isActive, variantName, feature, track])

  const { payload, hasInvalidJsonPayload } = useMemo(
    () => parsePayload<TPayload>(payloadType, payloadValue),
    [payloadType, payloadValue]
  )

  useEffect(() => {
    if (!shouldReportIssues || !hasInvalidJsonPayload) {
      return
    }

    reportExperimentIssue(`Invalid experiment payload JSON for ${feature} (${variantName})`)
  }, [feature, hasInvalidJsonPayload, shouldReportIssues, variantName])

  // Report missing copy for a live non-original variant.
  const reportMissingCopy = useCallback(
    (label: string) => {
      if (!shouldReportIssues) {
        return
      }

      reportExperimentIssue(`Missing experiment copy for ${feature} (${variantName}) at "${label}"`)
    },
    [feature, shouldReportIssues, variantName]
  )

  return {
    enabled: isActive,
    variant: variantName,
    payload: isActive ? payload : undefined,
    reportMissingCopy
  }
}
